import React, { useState } from 'react';
import TextField from '@mui/material/TextField';
import InputAdornment from '@mui/material/InputAdornment';
import EDIconButton from '../Buttons/IconButton/EDIconButton';
import EDDialog from '../Dialog/EDDialog';
import EDBox from '../EDBox/EDBox';
import { useModelTree, IModelTreeNode } from './ModelTreeContext';
import { SimpleTreeView, TreeItem } from '@mui/x-tree-view-pro';
import EDSwitch from '../Switch/EDSwitch';
import EDFormControlLabel from '../FormControlLabel/EDFormControlLabel';
import EDDivider from '../Divider/EDDivider';
import { useIsTouchDevice } from '../../Components/BreakpointContext/BreakpointContextUtils';
import { useIsExtraSmallScreen } from '../../Hooks/useFullscreenView';
import EDTooltip from '../Tooltip/EDTooltip';

export interface IRapporteditorTextFieldProps {
  label: string;
  hint?: string;
  value: any;
  onChange: (ctrl: any, selectedNode?: IModelTreeNode) => void;
  field: string;
  numeric?: boolean;
  allowModelSelect?: boolean;
  selectAttribute?: boolean;
  selectAssociation?: boolean;
  selectFromAssociation?: string;
}

const nodeId = (node: IModelTreeNode) =>
  node.path + (node.children && node.children.length > 0 ? '...' : '');

export const RapporteditorTextField: React.FC<
  IRapporteditorTextFieldProps
> = props => {
  const [dialogOpen, setDialogOpen] = useState(false);
  const [selectionState, setSelectionState] = useState<{
    start: number;
    end: number;
  } | null>(null);
  const [selectedNode, setSelectedNode] = useState<IModelTreeNode | null>(null);
  const [expanded, setExpanded] = useState<string[]>([]);
  const [showAllAttributes, setShowAllAttributes] = useState<boolean>(false);
  const modelTree = useModelTree();
  const isExtraSmallScreen = useIsExtraSmallScreen();
  const isTouchDevice = useIsTouchDevice();
  const fieldValue = props.value[props.field] ?? '';
  const inputRef = React.useRef<HTMLInputElement>(null);

  const handleOpenDialog = () => {
    // Save current cursor position and selection
    if (inputRef.current) {
      const start = inputRef.current.selectionStart ?? 0;
      const end = inputRef.current.selectionEnd ?? 0;
      setSelectionState({ start, end });
    }
    setDialogOpen(true);
  };

  const handleCloseDialog = () => {
    setDialogOpen(false);
    setSelectionState(null);
    setSelectedNode(null);
    setExpanded([]);
  };

  const findNodeById = (
    nodes: IModelTreeNode[],
    id: string
  ): IModelTreeNode | null => {
    for (const node of nodes) {
      const itemId = nodeId(node);
      if (itemId === id) {
        return node;
      }
      if (node.children) {
        const found = findNodeById(node.children, id);
        if (found) return found;
      }
    }
    return null;
  };

  const handleNodeSelect = (
    event: React.SyntheticEvent<Element, Event> | null,
    nodeId: string | null
  ) => {
    if (!nodeId) {
      setSelectedNode(null);
      return;
    }
    const node = findNodeById(modelTree, nodeId);
    if (props.selectAssociation) {
      if (node && !node.isExtendedAssociation) return;
    } else {
      if (node && node.children && node.children.length > 0) return;
      if (
        node &&
        props.selectFromAssociation &&
        !node.path.startsWith(props.selectFromAssociation + '.')
      )
        return;
    }
    setSelectedNode(node);
  };

  const handleToggle = (
    event: React.SyntheticEvent<Element, Event> | null,
    nodeIds: string[]
  ) => {
    setExpanded(nodeIds || []);
  };

  const handleSelectModel = (selectedNode: IModelTreeNode) => {
    if (selectionState && inputRef.current) {
      if (props.selectAttribute || props.selectAssociation) {
        // Update the value
        props.onChange(
          { ...props.value, [props.field]: selectedNode.path },
          selectedNode
        );
        // Set focus back to input
        setTimeout(() => {
          if (inputRef.current) {
            inputRef.current.focus();
          }
        }, 0);
      } else if (props.selectFromAssociation) {
        // Update the value (using the part of the path following the association)
        const newPath = selectedNode.path.split('.').slice(1).join('.');
        props.onChange(
          { ...props.value, [props.field]: newPath },
          selectedNode
        );
        // Set focus back to input
        setTimeout(() => {
          if (inputRef.current) {
            inputRef.current.focus();
          }
        }, 0);
      } else {
        const selectedModelText = `{${selectedNode.path}}`;
        const currentValue = fieldValue;
        let startPos = selectionState.start;
        let endPos = selectionState.end;

        // Check if the selection is surrounded by {}
        const hasLeftBrace = startPos > 0 && currentValue[startPos - 1] === '{';
        const hasRightBrace =
          endPos < currentValue.length && currentValue[endPos] === '}';

        // If the selection is surrounded by {}, include these in the replacement as well
        if (hasLeftBrace && hasRightBrace) {
          startPos = startPos - 1;
          endPos = endPos + 1;
        }

        const before = currentValue.substring(0, startPos);
        const after = currentValue.substring(endPos);
        const newValue = before + selectedModelText + after;

        // Update the value
        props.onChange(
          { ...props.value, [props.field]: newValue },
          selectedNode
        );

        // Set cursor position after the inserted text
        setTimeout(() => {
          if (inputRef.current) {
            const newCursorPos = startPos + selectedModelText.length;
            inputRef.current.setSelectionRange(newCursorPos, newCursorPos);
            inputRef.current.focus();
          }
        }, 0);
      }
    }
    setDialogOpen(false);
    setSelectionState(null);
  };

  return (
    <EDBox sx={{ mb: 2 }}>
      <EDTooltip title={props.hint || ''}>
        <TextField
          label={props.label}
          value={fieldValue}
          onChange={e =>
            props.onChange({
              ...props.value,
              [props.field]: e.target.value
                ? props.numeric
                  ? Number(e.target.value)
                  : e.target.value
                : undefined,
            })
          }
          fullWidth
          size="small"
          inputRef={inputRef}
          inputProps={
            props.numeric ? { maxLength: 4, pattern: '\\d*' } : undefined
          }
          InputProps={{
            endAdornment: props.allowModelSelect ? (
              <InputAdornment position="end">
                <EDIconButton
                  onClick={handleOpenDialog}
                  iconSize="medium"
                  icon="add_circle_outlined"
                  iconColor="action"
                  tooltip="Välj fält från modellen"
                  tooltipInAriaLabelOnly
                  disableTabbing={true}
                  margin="none"
                  padding="none"
                />
              </InputAdornment>
            ) : undefined,
          }}
        />
      </EDTooltip>

      {props.allowModelSelect && (
        <EDDialog
          open={dialogOpen}
          onClose={handleCloseDialog}
          title="Välj fält från modellen"
          maxWidth="sm"
          footerActions={[
            {
              onClick: handleCloseDialog,
              label: 'Avbryt',
            },
            {
              onClick: () => {
                if (selectedNode) {
                  handleSelectModel(selectedNode);
                }
              },
              label: 'Välj',
              buttonProps: {
                color: 'primary',
                disabled: !selectedNode,
              },
            },
          ]}
        >
          <EDBox>
            <EDFormControlLabel
              control={
                <EDSwitch
                  checked={showAllAttributes}
                  onChange={e => setShowAllAttributes(e.target.checked)}
                />
              }
              label="Visa alla fält"
            />
            <EDDivider />
            <SimpleTreeView
              expandedItems={expanded}
              selectedItems={selectedNode ? selectedNode.path : null}
              onExpandedItemsChange={handleToggle}
              onSelectedItemsChange={handleNodeSelect}
              sx={{
                paddingTop: 2,
                paddingBottom: 2,
                ...(isExtraSmallScreen
                  ? {
                      height: '100%',
                      width: '100%',
                    }
                  : {
                      minWidth: 500,
                    }),
              }}
            >
              {renderTreeItems(
                props.selectFromAssociation
                  ? modelTree.find(
                      item => item.name === props.selectFromAssociation
                    )?.children || []
                  : modelTree,
                Boolean(props.selectAssociation),
                showAllAttributes,
                isTouchDevice
              )}
            </SimpleTreeView>
          </EDBox>
        </EDDialog>
      )}
    </EDBox>
  );
};

const renderTreeItems = (
  nodes: IModelTreeNode[],
  selectAssociation: boolean,
  showAllAttributes: boolean,
  isTouchDevice: boolean
): React.ReactNode => {
  return nodes
    .filter(
      node =>
        (selectAssociation
          ? node.isExtendedAssociation
          : !node.isExtendedAssociation) &&
        (node.exportable || showAllAttributes)
    )
    .map(node => {
      const itemId = nodeId(node);
      return (
        <TreeItem
          key={itemId}
          itemId={itemId}
          label={node.displayName}
          sx={isTouchDevice ? { paddingTop: 1, paddingBottom: 1 } : undefined}
        >
          {node.children &&
            !selectAssociation &&
            node.children.length > 0 &&
            renderTreeItems(
              node.children,
              selectAssociation,
              showAllAttributes,
              isTouchDevice
            )}
        </TreeItem>
      );
    });
};
